'use client';

import CoatingPrice from '@/app/_lens-components/components/CoatingPrice';
import ExplainDialog from '@/app/_lens-components/components/ExplainDialog';
import { COATING_COMPONENT } from '@/app/_lens-components/constants';
import { useDefaultSelection } from '@/app/_lens-components/DefaultSelectionContext';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { resolveLensTypeTrackingFields } from '@/app/_lens-components/tracking';
import { CoatingItem } from '@/app/_lens-components/types';
import { sensorParseNumber, sensorParseString } from '@/utils/sensor-parse';
import {
  getApiPrescriptionType,
  handleCoatingPriceInfo,
  pickDefaultCoatingItem,
} from '@/app/_lens-components/utils';
import {
  mergePrevLensSelection,
  readPrevLensSelection,
} from '@/app/_lens-components/utils/operatePrevLensSelection';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useWhenCurReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { processLineBreaks } from '@/utils/tools';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';

export default function Coating({
  coatingList,
  selectedCoating,
  onSelectedCoating,
}: {
  coatingList: CoatingItem[];
  selectedCoating: CoatingItem | null;
  onSelectedCoating: (coating: CoatingItem) => void;
}) {
  const { canModifyPrevSelection, hasDefaultSelection } = useDefaultSelection();
  const t = useTranslations('lens-process');
  const coatingTitle = useOpticalProcedureTitle('coating', t('coating'));
  const recommendedTitle = useOpticalProcedureTitle('recommended', t('recommendedForYou'));
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const waiMode = useStore((state) => state.waiMode);
  const { trackEvent } = useReporting();
  const {
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    selectedLensIndexItem,
    selectedColorItem,
    selectedAdvancedLensIndexItem,
    lensTypePriceInfo,
    lensTypeForTracking,
    selectedLensType,
    selectedBrand,
    nowComponent,
  } = useLensProcessStore();
  /** 与 lens-process-store 里加购/折射率埋点同源（含高级渐进单条 selectedAdvancedLensIndexItem） */
  let selectedLensForSensor = selectedAdvancedLensIndexItem ?? selectedLensIndexItem;
  if (!selectedLensForSensor) {
    // * 到了镀膜环节，没有选择折射率，不是真的没有折射率，而是只有一个折射率不用选
    selectedLensForSensor = selectedColorItem?.lens_list[0];
  }
  const lensTypeTrackingFields = resolveLensTypeTrackingFields({
    lensType: selectedLensType,
    brand: selectedBrand,
    advancedLensIndexItem: selectedAdvancedLensIndexItem,
    lensTypeForTracking,
  });
  const lensPackageSensorProps = {
    lens_code: sensorParseString(selectedLensForSensor?.code),
    lens_id: sensorParseString(selectedLensForSensor?.id),
    lens_color: sensorParseString(selectedColorItem?.color_name ?? selectedColorItem?.lens_color),
    lens_price: sensorParseNumber(lensTypePriceInfo?.price),
    lens_type: sensorParseString(lensTypeTrackingFields.lens_type),
    lens_type_name: sensorParseString(lensTypeTrackingFields.lens_type_name),
    lens_brand: sensorParseString(lensTypeTrackingFields.lens_brand),
    lens_brand_name: sensorParseString(lensTypeTrackingFields.lens_brand_name),
    current_lens_price: sensorParseNumber(lensTypePriceInfo?.sell_price),
  };
  const isWap = useMediaQuery('(max-width:1280px)');
  const [showExplainPop, setShowExplainPop] = useState(false);
  const [explainContent, setExplainContent] = useState<CoatingItem>(null);
  const coatingListRef = useRef<HTMLUListElement | null>(null);

  // 小语种默认勾选 + 货币就绪后自动写入镀膜（与 getCoatingList 里 initDefaultCoatingSelection 一致）
  useWhenCurReady(() => {
    if (
      !hasDefaultSelection ||
      nowComponent !== COATING_COMPONENT ||
      selectedCoating ||
      !coatingList?.length
    )
      return;
    const prev = readPrevLensSelection();
    const pick = pickDefaultCoatingItem(coatingList, currency, userInfo, prev.prevCoatingId);
    if (pick) onSelectedCoating(pick);
  }, [
    hasDefaultSelection,
    nowComponent,
    selectedCoating,
    coatingList,
    currency,
    userInfo,
    onSelectedCoating,
  ]);

  useLayoutEffect(() => {
    if (!selectedCoating?.id) return;
    const selectedEl = coatingListRef.current?.querySelector<HTMLElement>(
      `[data-coating-id="${selectedCoating.id}"]`,
    );
    selectedEl?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
      inline: 'nearest',
    });
  }, [selectedCoating?.id, coatingList.length, nowComponent]);

  // 处理点击选择涂层
  const handleItemClick = (item: any) => {
    // 选中的和已选中的一样，不做处理
    if (selectedCoating && item.id === selectedCoating.id) {
      return;
    }
    onSelectedCoating(item);
    if (canModifyPrevSelection && item?.id != null) {
      mergePrevLensSelection({ prevCoatingId: `${item.id}` });
    }
    SDLensCoatingPageSelectClick(item);
  };

  // 显示说明弹窗
  const showExplain = (item: CoatingItem) => {
    setExplainContent(item);
    setShowExplainPop(true);
  };

  useEffect(() => {
    // 埋点：页面浏览
    try {
      trackEvent(
        {
          eventName: 'CoatingPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            ...lensPackageSensorProps,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }, []);

  function SDLensCoatingPageSelectClick(coatingItem: CoatingItem) {
    try {
      const coatingPriceInfo = handleCoatingPriceInfo(coatingItem, currency, userInfo);
      trackEvent(
        {
          eventName: 'CoatingPage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            coating_id: `${coatingItem.id}`,
            coating_type_name: coatingItem.name,
            coating_price: parseFloat(coatingPriceInfo.price),
            current_coating_price: parseFloat(coatingPriceInfo.sell_price),
            currency: currency.name,
            ...lensPackageSensorProps,
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  return (
    <div className="font-br w-full h-full box-border overflow-y-auto">
      <h3 className={stepCommonClassName.stepTitle}>
        <span>{coatingTitle}</span>
      </h3>

      {Array.isArray(coatingList) && coatingList.length > 0 ? (
        <ul className={stepCommonClassName.optionsContainer} ref={coatingListRef}>
          {coatingList.map((item, index) => (
            <li
              key={index}
              data-coating-id={item.id}
              className={`${stepCommonClassName.optionItem} ${
                item.recommend === 1 ? stepCommonClassName.recommendedItem : ''
              }`}
            >
              {item.recommend === 1 && (
                <div className={stepCommonClassName.recommendBadge}>
                  <div className={stepCommonClassName.recommendBadgeContent}>
                    <span>{recommendedTitle}</span>
                  </div>
                </div>
              )}

              <div
                className={`${stepCommonClassName.optionBox} ${
                  selectedCoating?.id === item.id ? stepCommonClassName.selectedOptionBox : ''
                } cursor-pointer`}
                onClick={() => handleItemClick(item)}
              >
                <div className={`!items-center ${stepCommonClassName.optionContent} p-[14px_20px]`}>
                  <div className={`${stepCommonClassName.imageContainer} min-w-[60px]`}>
                    <Image
                      src={item.img}
                      className="w-[60px] h-[60px]"
                      alt={item.name}
                      width={60}
                      height={60}
                    />
                  </div>

                  <div className={stepCommonClassName.textContainer}>
                    <div className={stepCommonClassName.textCenterContainer}>
                      <div className={stepCommonClassName.titleContainer}>
                        <p>{item.name}</p>
                        {item.explain && (
                          <Tooltip
                            title={
                              <p
                                className="text-[14px]"
                                dangerouslySetInnerHTML={{
                                  __html: processLineBreaks(item.explain),
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              />
                            }
                            placement="bottom"
                            variant="light"
                            sx={{
                              zIndex: 1500,
                              '& .MuiTooltip-tooltip': {
                                fontSize: '14px',
                                maxWidth: '490px',
                              },
                            }}
                          >
                            <QuestionResponsive
                              className={stepCommonClassName.questionIcon}
                              onClick={(e) => {
                                isWap && showExplain(item);
                                e.stopPropagation();
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>

                      <div className={stepCommonClassName.description(waiMode)}>
                        <p>{item.description}</p>
                      </div>
                    </div>

                    <div className={stepCommonClassName.priceContainer}>
                      <CoatingPrice
                        currency={currency}
                        coatingItem={item}
                        renderPrice={(price, sellPrice) => (
                          <span className={stepCommonClassName.priceInnerBox}>
                            {Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                              <span className={stepCommonClassName.priceText}>{sellPrice}</span>
                            ) : (
                              <span className={stepCommonClassName.priceText}>{t('free')}</span>
                            )}
                            {price !== sellPrice && (
                              <span className={stepCommonClassName.originalPriceText(waiMode)}>
                                {price}
                              </span>
                            )}
                          </span>
                        )}
                      />

                      <CoatingPrice
                        currency={currency}
                        coatingItem={item}
                        renderPrice={(price, sellPrice, discountPercent, hasMemberPrice) => {
                          if (discountPercent > 0) {
                            return (
                              <div
                                className={`absolute top-0 right-0 h-[15px] p-[0px_6px] ${(() => {
                                  if (!hasMemberPrice) return 'bg-[#fca6ff]';

                                  switch (userInfo.member_level) {
                                    case 0:
                                      return 'bg-[#cad8fc]';
                                    case 1:
                                      return 'bg-[#b2cef0]';
                                    case 2:
                                      return 'bg-[#f7bd9e]';
                                    case 3:
                                      return 'bg-gradient-to-r from-[#c693ff] to-[#cbeaff]';
                                    default:
                                      return 'bg-[#fca6ff]';
                                  }
                                })()} rounded-[0px_4px_0px_6px] text-[11px] font-br flex items-center justify-center`}
                              >
                                <span className="relative top-[1px]">
                                  {t('discountPercentOff', { discountPercent })}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className={stepCommonClassName.emptyState}>
          <Image src="/null.png" alt="No coating available" width={118} height={118} />
          <p className={stepCommonClassName.emptyStateText}>{t('sorryNoCoatingAvailable')}</p>
        </div>
      )}

      <ExplainDialog
        title={explainContent?.name || ''}
        open={showExplainPop}
        onClose={() => setShowExplainPop(false)}
      >
        <p
          className="text-sm font-br"
          dangerouslySetInnerHTML={{
            __html: processLineBreaks(explainContent?.explain),
          }}
        />
      </ExplainDialog>
    </div>
  );
}
